import React, { useEffect, useRef, useState } from "react";

/**
 * Animated count-up number. Activates when scrolled into view.
 * If `value` is not numeric (e.g. "∞"), it renders the static string.
 */
export const CountUp = ({ value, duration = 1800, className = "" }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const startedRef = useRef(false);

  // Parse numeric portion (e.g. "10+" -> 10, suffix "+")
  const match = String(value).match(/^(\d+)(.*)$/);
  const targetNum = match ? parseInt(match[1], 10) : null;
  const suffix = match ? match[2] : "";

  useEffect(() => {
    if (targetNum === null) {
      setDisplay(value);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now) => {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
              const current = Math.round(eased * targetNum);
              setDisplay(`${current}${suffix}`);
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [targetNum, suffix, duration, value]);

  return (
    <span ref={ref} className={className}>
      {targetNum === null ? value : display}
    </span>
  );
};

export default CountUp;
